import { EFFECT_TYPES as E } from '../../constants/effects';

/**
 * Where and how a reaction happens, shown under the result.
 */
export const CONDITIONS = Object.freeze({
  ROOM: 'At room temperature',
  SLOW_AIR: 'Slowly in air, faster when heated',
  HEAT: 'When heated',
  STRONG_HEAT: 'Only when heated very strongly',
  FURNACE: 'Only in a very hot furnace',
  SPARK: 'With a spark or a flame',
  LIGHT: 'In sunlight or bright light',
  LIGHTNING: 'Only in lightning or a hot car engine',
  FACTORY: 'In a factory, with high pressure and a catalyst',
  STEAM: 'Only with hot steam',
  MELT: 'When melted together',
  GRINDING: 'When ground together',
  SPECIAL_LAB: 'Only in a special research lab',
  NUCLEAR_LAB: 'Only in a special nuclear lab (it is radioactive)',
  NEVER: 'Never tried: not enough atoms exist',
  NONE: 'Nothing happens, even when heated',
  NOT_MIXED: 'Nothing happens when they are simply mixed',
});

const HALOGEN_SAFETY = 'Halogen gases like chlorine and fluorine are poisonous. This is only for professional labs.';
const FIRE_SAFETY = 'Burning chemicals are extremely hot and can hurt your eyes. Only watch real ones with a grown-up and safety glasses.';
const REACTIVE_METAL_SAFETY = 'Only trained scientists should handle these metals. They can catch fire in water.';
const ACID_SAFETY = 'Acids can burn skin and eyes. Always wear goggles and gloves.';
const MELT_SAFETY = 'Melted metal is dangerously hot. Only experts melt metals, in special furnaces.';
const POISON_SAFETY = 'Many of these compounds are poisonous. Only professional chemists make them.';

/**
 * Every kind of result the reaction engine can produce. The spoken texts never name the
 * substances, so each one is recorded once as a voice clip and reused for every pair.
 */
export const REACTION_CLASSES = Object.freeze({
  'oxide-burn': {
    effect: E.GLOW,
    conditions: CONDITIONS.HEAT,
    title: 'Whoosh! It burned in oxygen!',
    explanation:
      'When it gets hot enough, this element grabs oxygen from the air and burns with a bright flame. The atoms lock together into a brand new solid called an oxide.',
    funFact: 'Fireworks get many of their colours from metals burning in oxygen!',
    safetyNote: FIRE_SAFETY,
  },
  'oxide-slow': {
    effect: E.SLOW_CHANGE,
    conditions: CONDITIONS.HEAT,
    title: 'Slowly, a new coat appears',
    explanation:
      "Oxygen slowly joins onto the metal's surface and makes an oxide, a bit like rusting. It can take days, or it may need some heat to get going.",
    funFact: 'The green colour on old copper roofs and statues comes from a slow reaction with the air!',
  },
  'noble-oxygen': {
    effect: E.NONE,
    conditions: CONDITIONS.NONE,
    title: "It won't rust!",
    explanation:
      "Oxygen can't pull electrons away from this metal, so it doesn't rust or burn in air. That's why metals like this are used for jewellery and electronics.",
    funFact: 'Gold treasure pulled from ancient shipwrecks still shines like new!',
  },
  'halide-flash': {
    effect: E.FLASH,
    conditions: CONDITIONS.ROOM,
    title: 'FLASH! A salt was made!',
    explanation:
      'The metal hands its outer electrons to the halogen, which is desperate to grab them. The swap is so fast it makes a bright flash and lots of heat, and a salt is left behind.',
    funFact: 'The word halogen means "salt maker" in Greek!',
    safetyNote: HALOGEN_SAFETY,
  },
  'halide-heat': {
    effect: E.GLOW,
    conditions: CONDITIONS.HEAT,
    title: 'Hot swap! A new salt',
    explanation:
      'When heated, the metal gives electrons to the halogen. The two stick together tightly as a salt called a halide.',
    funFact: 'Table salt is a halide too: sodium chloride!',
    safetyNote: HALOGEN_SAFETY,
  },
  'halide-slow': {
    effect: E.SLOW_CHANGE,
    conditions: CONDITIONS.HEAT,
    title: 'A slow, steady reaction',
    explanation:
      'This metal is very calm, but halogens are so eager that they still manage to pull a few electrons away. Slowly, a thin layer of salt forms.',
    funFact: 'Old-fashioned photographs used silver halides, which darken when light hits them!',
    safetyNote: HALOGEN_SAFETY,
  },
  'sulfide-flash': {
    effect: E.FLASH,
    conditions: CONDITIONS.HEAT,
    title: 'Poof! A sulfide flash!',
    explanation:
      'Once they get hot, sulfur and this metal join so fast that the mixture flashes and smokes. A new solid called a sulfide is left behind.',
    funFact: 'Lots of shiny rocks, like fool\'s gold, are sulfides!',
    safetyNote: 'Burning sulfur makes a choking gas. Never try this at home.',
  },
  'sulfide-heat': {
    effect: E.GLOW,
    conditions: CONDITIONS.HEAT,
    title: 'Heat it up: a sulfide!',
    explanation:
      'When heated, sulfur joins the metal and they glow as they react, making a sulfide. Many metals are dug out of the ground as sulfide ores.',
    funFact: 'Miners find many metals in the ground joined to sulfur, as ores.',
    safetyNote: 'Heated sulfur makes a choking gas. Never try this at home.',
  },
  'sulfide-slow': {
    effect: E.SLOW_CHANGE,
    conditions: CONDITIONS.ROOM,
    title: 'Tarnish time!',
    explanation:
      "Sulfur slowly joins the metal's surface and makes a dark layer. This is called tarnish, and it's why silver spoons turn black over time.",
    funFact: 'Eggs and onions give off tiny amounts of sulfur, enough to tarnish silver!',
  },
  chalcogenide: {
    effect: E.GLOW,
    conditions: CONDITIONS.HEAT,
    title: "Sulfur's cousin joins in!",
    explanation:
      "Selenium, tellurium and polonium are sulfur's cousins in the periodic table. When heated with this metal, they join up just like sulfur does and make a new solid.",
    funFact: 'Compounds like these are used in solar panels and in tiny glowing dots inside TV screens!',
  },
  nitride: {
    effect: E.GLOW,
    conditions: CONDITIONS.STRONG_HEAT,
    title: 'Nitrogen grabbed on!',
    explanation:
      'Nitrogen is usually very lazy, but when things get very hot this metal is strong enough to pull it apart and make a nitride.',
    funFact: 'Titanium nitride is gold-coloured and coats drill bits to make them tough!',
  },
  hydride: {
    effect: E.SLOW_CHANGE,
    conditions: CONDITIONS.HEAT,
    title: 'Hydrogen hid inside!',
    explanation: 'When heated in hydrogen gas, this metal soaks up hydrogen atoms and turns into a new solid called a hydride.',
    funFact: 'Some hydrides can store hydrogen fuel for clean cars!',
    safetyNote: 'Hydrogen gas can explode near flames. Only experts work with it.',
  },
  carbide: {
    effect: E.GLOW,
    conditions: CONDITIONS.FURNACE,
    title: 'Super hot, super hard!',
    explanation:
      'Only in a very hot furnace do carbon and this metal join, making a carbide. Many carbides are among the hardest materials ever made.',
    funFact: 'Tungsten carbide is so hard it is used for drill tips and the tiny balls in ballpoint pens!',
  },
  'boride-silicide': {
    effect: E.GLOW,
    conditions: CONDITIONS.FURNACE,
    title: 'Furnace chemistry!',
    explanation:
      'In a very hot furnace, boron or silicon atoms squeeze in between the metal atoms, making a tough compound that can survive extreme heat.',
    funFact: 'Magnesium boride becomes a superconductor, carrying electricity with no loss, when it is very cold!',
  },
  'phosphide-arsenide': {
    effect: E.GLOW,
    conditions: CONDITIONS.HEAT,
    title: 'A tricky compound',
    explanation:
      'When heated together, phosphorus or arsenic joins this metal to make a new solid. Some compounds like this are used to make LEDs and computer chips.',
    funFact: 'Many LED lights shine thanks to compounds like gallium arsenide!',
    safetyNote: POISON_SAFETY,
  },
  alloy: {
    effect: E.MELT,
    conditions: CONDITIONS.MELT,
    title: 'Metal mix-up!',
    explanation:
      "Metals don't swap electrons with each other the way metals and non-metals do. But if you melt them together, they can blend into an alloy, a metal mixture that can be stronger or shinier than either one alone.",
    funFact: 'Steel, bronze and brass are all alloys!',
    safetyNote: MELT_SAFETY,
  },
  amalgam: {
    effect: E.MELT,
    conditions: CONDITIONS.ROOM,
    title: 'A mercury mix!',
    explanation:
      'Mercury is a liquid metal, and many other metals dissolve in it, a bit like sugar in tea. The shiny mixture is called an amalgam.',
    funFact: 'Dentists used to fill teeth with an amalgam made of mercury and silver!',
    safetyNote: 'Mercury is poisonous. Never touch it.',
  },
  'water-explosive': {
    effect: E.EXPLOSION,
    conditions: CONDITIONS.ROOM,
    title: 'BANG! It exploded in water!',
    explanation:
      'This metal is so desperate to give away its outer electron that it rips water apart instantly. The heat sets the hydrogen gas on fire with a loud bang!',
    funFact: 'The further down the first column of the periodic table you go, the wilder the reaction with water gets.',
    safetyNote: 'Never try this! These metals can explode when they touch water.',
  },
  'water-violent': {
    effect: E.FIZZ,
    conditions: CONDITIONS.ROOM,
    title: 'Zoom! It raced across the water!',
    explanation:
      'The metal swaps its electron with water, making hydrogen gas and so much heat that it melts into a ball and whizzes around on the surface.',
    funFact: 'The water left behind becomes an alkali, the opposite of an acid.',
    safetyNote: REACTIVE_METAL_SAFETY,
  },
  'water-fizz': {
    effect: E.FIZZ,
    conditions: CONDITIONS.ROOM,
    title: 'Bubbles fizz in the water!',
    explanation:
      'The metal steadily swaps electrons with water. Bubbles of hydrogen gas rise, and a hydroxide makes the water cloudy.',
    funFact: 'The cloudy stuff is an alkali, like the ones in soap and cleaning products.',
    safetyNote: REACTIVE_METAL_SAFETY,
  },
  'water-slow': {
    effect: E.FIZZ,
    conditions: CONDITIONS.ROOM,
    title: 'Tiny bubbles, slowly',
    explanation: 'In cold water only a few bubbles of hydrogen appear. In hot water the metal reacts much faster.',
    funFact: 'Warmth speeds up almost every chemical reaction!',
  },
  'water-steam': {
    effect: E.SLOW_CHANGE,
    conditions: CONDITIONS.STEAM,
    title: 'Only with steam!',
    explanation:
      'Cold water does nothing. But blast this hot metal with steam and it pulls the oxygen out of the water, leaving hydrogen gas behind.',
    funFact: 'Long ago, hydrogen for balloons was made by passing steam over red-hot iron!',
  },
  'water-none': {
    effect: E.NONE,
    conditions: CONDITIONS.NOT_MIXED,
    title: 'No splash, no change',
    explanation: "This one ignores water completely. It doesn't react just by getting wet.",
    funFact: "Metals that ignore water are great for making pipes, coins and jewellery.",
  },
  'acid-violent': {
    effect: E.FIZZ,
    conditions: CONDITIONS.ROOM,
    title: 'Whoa! Wild fizzing!',
    explanation:
      "Acids are full of hydrogen that this metal is desperate to kick out. It reacts so fast that the acid bubbles furiously and gets very hot.",
    funFact: 'The gas bubbling out is hydrogen, the lightest gas in the universe.',
    safetyNote: ACID_SAFETY,
  },
  'acid-fizz': {
    effect: E.FIZZ,
    conditions: CONDITIONS.ROOM,
    title: 'Hydrogen bubbles fizz up!',
    explanation:
      'The acid pulls electrons away from the metal. The metal dissolves into a salt, and bubbles of hydrogen gas fizz up.',
    funFact: 'Scientists rank metals by how fast they fizz in acid.',
    safetyNote: ACID_SAFETY,
  },
  'acid-slow': {
    effect: E.SLOW_CHANGE,
    conditions: CONDITIONS.ROOM,
    title: 'Slow, lazy bubbles',
    explanation:
      'Only a few bubbles appear, and the metal dissolves very slowly. Some metals grow a thin skin that holds the acid back.',
    funFact: 'Heating the acid makes slow metals react much faster.',
    safetyNote: ACID_SAFETY,
  },
  'acid-none': {
    effect: E.NONE,
    conditions: CONDITIONS.NOT_MIXED,
    title: "The acid can't touch it",
    explanation:
      "This one holds on to its electrons more tightly than hydrogen does, so this acid can't make it react.",
    funFact: 'Copper, silver and gold are called noble metals partly because acids like this leave them alone.',
  },
  'covalent-burn': {
    effect: E.GLOW,
    conditions: CONDITIONS.SPARK,
    title: 'It burned!',
    explanation:
      'Once lit, these two non-metals join by sharing electrons, giving off heat and light. The result is a brand new molecule.',
    funFact: 'Most of the fires you see are non-metals burning in oxygen.',
    safetyNote: FIRE_SAFETY,
  },
  'covalent-heat': {
    effect: E.SLOW_CHANGE,
    conditions: CONDITIONS.HEAT,
    title: 'Sharing electrons',
    explanation:
      "When heated, these two non-metals share electrons to build a new molecule. Neither one is a metal, so it's sharing, not swapping.",
    funFact: 'Sharing electrons is called a covalent bond. It holds together water, sugar and even you!',
    safetyNote: POISON_SAFETY,
  },
  'covalent-furnace': {
    effect: E.GLOW,
    conditions: CONDITIONS.FURNACE,
    title: 'Made in a furnace',
    explanation:
      'Only at furnace temperatures do these two join, building a giant, rock-hard network of atoms.',
    funFact: 'Some compounds made like this are nearly as hard as diamond!',
  },
  'covalent-fluorine': {
    effect: E.FLASH,
    conditions: CONDITIONS.ROOM,
    title: 'Fluorine attacks!',
    explanation:
      'Fluorine is the most reactive element of all. It tears into its partner and grabs as many electrons as it can, making a new compound in a flash.',
    funFact: 'Fluorine can even make compounds with some noble gases, which almost nothing else can do!',
    safetyNote: 'Fluorine is so dangerous that only a few expert labs ever use it.',
  },
  'halogen-halogen': {
    effect: E.SLOW_CHANGE,
    conditions: CONDITIONS.ROOM,
    title: 'Halogen team-up',
    explanation: 'Two halogens can share electrons to make a new compound, usually a colourful gas, liquid or solid.',
    funFact: 'Compounds made of two halogens are called interhalogens.',
    safetyNote: HALOGEN_SAFETY,
  },
  'noble-gas': {
    effect: E.NONE,
    conditions: CONDITIONS.NONE,
    title: 'Too happy to react',
    explanation:
      "Noble gases already have a full set of electrons, so they don't want to share or swap with anyone. That's why nothing happened!",
    funFact: 'Neon signs glow because noble gases light up in electricity instead of reacting.',
  },
  'too-rare': {
    effect: E.NONE,
    conditions: CONDITIONS.NEVER,
    title: 'Too rare to try!',
    explanation:
      'Only a handful of atoms of this element have ever existed at once, and many fall apart in less than a second. Nobody has ever had enough to mix it with anything and watch.',
    funFact: 'Many of these super-heavy elements were made by smashing atoms together in giant machines.',
  },
  'no-direct': {
    effect: E.NONE,
    conditions: CONDITIONS.NOT_MIXED,
    title: 'Nothing happened... this time!',
    explanation:
      "These two don't react in a way we can see in our lab. Try a different pair and see what happens!",
    funFact: 'Most pairs of substances just sit next to each other without reacting. Reactions need the right partners!',
  },
});

/**
 * Extra safety lines added on top of a reaction's own note.
 */
export const RADIOACTIVE_SAFETY_NOTE =
  'This one is radioactive! Scientists only handle it in special labs, with thick shielding.';
export const TOXIC_SAFETY_NOTE = 'This element and its compounds are poisonous. Only experts handle it, with great care.';
